import { Bank } from '@synapex/prisma/client'
import {
    ContragentResponse,
    CreateVkOrdContragentInputDto,
    UpdateVkOrdContragentInputDto,
} from '@synapex/shared2/gateway-rest-api'
import { WorkspaceDescriptor } from '@synapex/shared2/entities'
import { type FormValues, type ContragentType } from './schema'

type WorkspaceWithOptionalLink = WorkspaceDescriptor & { link_id?: number }

export const NAME_PREFIX: Record<ContragentType, string> = {
    juridical: '',
    ip: 'ИП ',
    physical: '',
    self_employed: '',
    autonomous_non_profit_organization: '',
}
export const DEFAULT_TYPE = 'physical'
export const DEFAULT_FORM_VALUES: FormValues = {
    inn: '',
    name: '',
    bic: '',
    kpp: '',
    ogrn: '',
    currentAccount: '',
    position: '',
    fullName: '',
    phone: '',
    email: '',
    passport: '',
    registrationAddress: '',
    actualAddress: '',
    legalAddress: '',
}

export const NAME_TEXT_VARIANTS = {
    juridical: {
        inputClassName: '',
        label: 'Название юр.лица',
        placeholder: ' Введите название юр.лица',
        additionalText: '',
    },
    ip: {
        inputClassName: 'pl-[22px]',
        label: 'ФИО',
        placeholder: 'Введите ФИО',
        additionalText: 'ИП',
    },
    physical: {
        inputClassName: '',
        label: 'ФИО',
        placeholder: 'Введите ФИО',
        additionalText: '',
    },
    self_employed: {
        inputClassName: '',
        label: 'ФИО',
        placeholder: 'Введите ФИО',
        additionalText: '',
    },
    autonomous_non_profit_organization: {
        inputClassName: '',
        label: 'Название юр.лица',
        placeholder: ' Введите название юр.лица',
        additionalText: '',
    },
}

// get name without prefix
export const getTrimmedName = (name: string, type: ContragentType) => {
    const prefix = NAME_PREFIX[type]

    if (!prefix) {
        return name
    }

    if (name.startsWith(prefix)) {
        return name.slice(prefix.length)
    }

    return name
}

// add prefix to name.
// if name already has prefix, return name
export const addPrefixToName = (name: string, type: ContragentType) => {
    if (name.startsWith(NAME_PREFIX[type])) {
        return name
    }
    return NAME_PREFIX[type] + name
}

export const makeCreateDto = (
    wd: WorkspaceWithOptionalLink,
    type: ContragentType,
    formValues: FormValues,
    bankInfo?: Bank | null,
    roleType: 'publisher' | 'advertiser' | 'agency' | 'ors' = 'publisher'
): CreateVkOrdContragentInputDto => {
    const {
        inn,
        name,
        currentAccount,
        position,
        fullName,
        bic,
        kpp,
        ogrn,
        email,
        passport,
        phone,
        registrationAddress,
        actualAddress,
        legalAddress,
    } = formValues

    const isJuridical =
        type === 'juridical' || type === 'autonomous_non_profit_organization'

    const result: CreateVkOrdContragentInputDto = {
        ...wd,

        roles: [roleType],
        type,
        inn,
        name: addPrefixToName(name, type),
    }

    if (bankInfo) {
        result.bic = bankInfo.bic
        result.correspondentAccount = bankInfo.account
    }

    if (currentAccount) {
        result.currentAccount = currentAccount
    }

    if (phone) {
        result.phone = phone
    }

    if (email) {
        result.email = email
    }

    if (isJuridical) {
        if (kpp) {
            result.kpp = kpp
        }

        if (ogrn) {
            result.ogrn = ogrn
        }

        if (position) {
            result.position = position
        }

        if (fullName) {
            result.fullName = fullName
        }

        if (bic) {
            result.bic = bic
        }

        if (legalAddress) {
            result.legalAddress = legalAddress
        }

        if (actualAddress) {
            result.actualAddress = actualAddress
        }
    } else {
        if (type === 'ip' && ogrn) {
            result.ogrn = ogrn
        }

        if (registrationAddress) {
            result.registrationAddress = registrationAddress
        }

        if (passport) {
            const passportData = parsePassport(passport)
            if (passportData) {
                result.passportSeries = passportData.series
                result.passportNumber = passportData.number
            }
        }
    }

    return result
}

export const makeUpdateDto = (
    wd: WorkspaceWithOptionalLink,
    type: ContragentType,
    source: ContragentResponse,
    formValues: FormValues,
    bankInfo?: Bank | null
): UpdateVkOrdContragentInputDto | null => {
    const dto: UpdateVkOrdContragentInputDto = {
        ...wd,
    }

    let hasChanges = false

    const isJuridical =
        type === 'juridical' || type === 'autonomous_non_profit_organization'

    if (source.inn !== formValues.inn) {
        dto.inn = formValues.inn
        hasChanges = true
    }

    if (source.name !== formValues.name || source.type !== type) {
        dto.name = addPrefixToName(formValues.name, type)
        hasChanges = true
    }

    if ((source.bic ?? '') !== formValues.bic) {
        if (bankInfo) {
            dto.bic = bankInfo.bic
            dto.correspondentAccount = bankInfo.account
        } else {
            dto.bic = null
            dto.correspondentAccount = null
        }

        hasChanges = true
    }

    if ((source.current_account ?? '') !== formValues.currentAccount) {
        dto.currentAccount = formValues.currentAccount
        hasChanges = true
    }

    if (source.type !== type) {
        dto.type = type
        hasChanges = true
    }

    if ((source.phone ?? '') !== formValues.phone) {
        dto.phone = formValues.phone || null
        hasChanges = true
    }

    if ((source.email ?? '') !== formValues.email) {
        dto.email = formValues.email || null
        hasChanges = true
    }

    if (isJuridical) {
        if ((source.kpp ?? '') !== formValues.kpp) {
            dto.kpp = formValues.kpp || null
            hasChanges = true
        }

        if ((source.ogrn ?? '') !== formValues.ogrn) {
            dto.ogrn = formValues.ogrn || null
            hasChanges = true
        }

        if ((source.position ?? '') !== formValues.position) {
            dto.position = formValues.position
            hasChanges = true
        }

        if ((source.full_name ?? '') !== formValues.fullName) {
            dto.fullName = formValues.fullName
            hasChanges = true
        }

        if (source.legal_address !== formValues.legalAddress) {
            dto.legalAddress = formValues.legalAddress || null
            hasChanges = true
        }

        if (source.actual_address !== formValues.actualAddress) {
            dto.actualAddress = formValues.actualAddress || null
            hasChanges = true
        }
    } else {
        if (type === 'ip' && (source.ogrn ?? '') !== formValues.ogrn) {
            dto.ogrn = formValues.ogrn || null
            hasChanges = true
        }

        if (
            (source.registration_address ?? '') !==
            formValues.registrationAddress
        ) {
            dto.registrationAddress = formValues.registrationAddress || null
            hasChanges = true
        }

        const currentPassport = formatPassportFromParts(
            source.passport_series,
            source.passport_number
        )
        if (currentPassport !== formValues.passport) {
            const passportData = parsePassport(formValues.passport)
            if (passportData) {
                dto.passportSeries = passportData.series
                dto.passportNumber = passportData.number
            } else {
                dto.passportSeries = null
                dto.passportNumber = null
            }
            hasChanges = true
        }
    }

    //formValues from handleSubmit already trimmed

    if (hasChanges) {
        return dto
    }

    return null
}

// Формирует строку паспорта из серии и номера
const formatPassportFromParts = (
    series: string | null,
    number: string | null
): string => {
    if (!series && !number) return ''
    if (series && number) return `${series} ${number}`
    return series || number || ''
}

// Парсит строку паспорта "1234 567890" на серию и номер
export const parsePassport = (
    passport: string
): { series: string; number: string } | null => {
    if (!passport) return null
    const parts = passport.split(' ')
    if (parts.length !== 2) return null
    const [series, number] = parts
    if (series.length !== 4 || number.length !== 6) return null
    return { series, number }
}

export const makeFormValues = (source: ContragentResponse): FormValues => {
    const {
        bic,
        name,
        inn,
        kpp,
        ogrn,
        current_account,
        position,
        full_name,
        phone,
        email,
        passport_series,
        passport_number,
        registration_address,
        legal_address,
        actual_address,
    } = source

    const trimmedName = getTrimmedName(name, source.type)

    return {
        bic: bic ?? '',
        name: trimmedName,
        inn,
        kpp: kpp ?? '',
        ogrn: ogrn ?? '',
        currentAccount: current_account ?? '',
        position: position ?? '',
        fullName: full_name ?? '',
        phone: phone ?? '',
        email: email ?? '',
        passport: formatPassportFromParts(passport_series, passport_number),
        registrationAddress: registration_address ?? '',
        legalAddress: legal_address ?? '',
        actualAddress: actual_address ?? '',
    }
}
